import { Box, Group, MantineTheme, ThemeIcon, Tooltip, useMantineTheme } from '@mantine/core';
import { IconCheck, IconFileCheck, IconPencil, IconTrashX, IconX } from '@tabler/icons-react';
import P2Regular from 'components/typography/P2Regular';

interface PriceOfferStatus {
  priceOfferStatusId: number;
  priceOfferStatusName: string;
}

interface PriceOfferStatusIconProps {
  status?: PriceOfferStatus;
  statusId?: number;
  textColorOverride?: string;
  iconOnly?: boolean;
  textOnly?: boolean;
  TextComponent?: React.FC<{ children: React.ReactNode; color?: string; lh?: number }>;
}

/**
 * Map from price-offer status id to icon.
 */
const STATUS_ICON_MAP: Record<number, (theme: MantineTheme) => React.ReactNode> = {
  1: (theme) => (
    <ThemeIcon radius="lg" size={24} color={theme.colors.gray[2]}>
      <IconPencil size={16} color={theme.colors.gray[7]} />
    </ThemeIcon>
  ),
  2: () => (
    <ThemeIcon radius="lg" size={24} color="#EDEFD8">
      <IconFileCheck size={16} color="#727920" />
    </ThemeIcon>
  ),
  3: (theme) => (
    <ThemeIcon radius="lg" size={24} color={theme.colors.green[9]}>
      <IconCheck size={16} color={theme.colors.green[0]} />
    </ThemeIcon>
  ),
  4: (theme) => (
    <ThemeIcon radius="lg" size={24} color={theme.colors.red[8]}>
      <IconX size={16} color={theme.colors.red[0]} />
    </ThemeIcon>
  ),
  999: (theme) => (
    <ThemeIcon radius="lg" size={24} color={theme.colors.red[1]}>
      <IconTrashX size={16} color={theme.colors.red[9]} />
    </ThemeIcon>
  ),
};

/**
 * Map from price-offer status id to text color.
 */
const STATUS_TEXT_COLOR_MAP: Record<number, string | undefined> = {
  1: 'gray.7',
  2: '#727920',
  3: 'green.9',
  4: 'red.8',
  999: 'red.9',
};

/**
 * Map from price-offer status id to text.
 */
export const PRICE_OFFER_STATUS_TEXT_MAP: Record<number, string | undefined> = {
  1: 'Vytvorená',
  2: 'Vyplnená',
  3: 'Vyhovujúca',
  4: 'Nevyhovujúca',
  999: 'Zmazaná',
};

/**
 * Renders a price-offer status using the same icon/text visual pattern as revision statuses.
 */
export default function PriceOfferStatusIcon({
  status,
  statusId: incomingStatusId,
  textColorOverride,
  iconOnly = false,
  textOnly = false,
  TextComponent = P2Regular,
}: PriceOfferStatusIconProps) {
  const theme = useMantineTheme();
  const statusId = status?.priceOfferStatusId ?? incomingStatusId ?? 1;
  const icon = STATUS_ICON_MAP[statusId]?.(theme) ?? STATUS_ICON_MAP[1](theme);
  const textColor = STATUS_TEXT_COLOR_MAP[statusId] ?? STATUS_TEXT_COLOR_MAP[1];
  const text = status?.priceOfferStatusName ?? PRICE_OFFER_STATUS_TEXT_MAP[statusId] ?? PRICE_OFFER_STATUS_TEXT_MAP[1];

  if (iconOnly) {
    return (
      <Tooltip withinPortal label={text}>
        <Box>{icon}</Box>
      </Tooltip>
    );
  }

  return (
    <Group spacing={8} align="center" noWrap>
      {textOnly ? null : icon}
      <TextComponent lh={1.4} color={textColorOverride ?? textColor}>
        {text}
      </TextComponent>
    </Group>
  );
}
